import axios from 'axios';
import axiosInstance from 'utils/axiosInstance';
import { print } from 'graphql/language/printer';
import { hasFeature } from 'utils/featureFlags';
import * as api from '../constants';
import {
  CategorySalesQuery,
  DiscountQuery,
  GratuityQuery,
  GrossProfitQuery,
  ItemSalesQuery,
  SalesSummaryQuery,
  SalesTypeQuery,
  ServedByQuery,
  CollectedByQuery,
  TaxesQuery,
  ModifierSalesQuery,
  PaymentMethodsQuery,
} from './queries.gql';

const fileDownload = require('react-file-download');

const FLAG_NAME_REPORT_USE_GRAPHQL = 'use-reporting-graphql-api';

function commonReject(error) {
  if (error.response) {
    const eobj = new Error(error.response.errors);
    return Promise.reject(eobj);
  }
  return Promise.reject(error);
}

/**
 *
 * @param {string} query
 * @param {Object} variables
 */
function queryReportingApi(query, variables) {
  return axiosInstance.post('/mkService/reporting-revamp/graphql', {
    query,
    variables,
  });
}

/**
 * @typedef {Object} SalesSummaryReport
 * @property {number} discount
 * @property {number} gratuity
 * @property {number} grossSales
 * @property {number} netSales
 * @property {number} redemption
 * @property {number} refund
 * @property {number} roundingAmount
 * @property {number} tax
 * @property {number} totalCollected
 *
 * @param {Object} data
 * @param {string} data.startDate
 * @param {string} data.endDate
 * @param {number[]} outletIds
 * @returns {Promise<SalesSummaryReport>}
 */
export function postSalesSummary(data, outletIds) {
  if (hasFeature(FLAG_NAME_REPORT_USE_GRAPHQL)) {
    const { startDate, endDate } = data;
    return queryReportingApi(print(SalesSummaryQuery), {
      startDate,
      endDate,
      outletIds,
    }).then(response => {
      // Still need to do transformation
      const { salesSummaryReport } = response.data.data;
      return {
        ...salesSummaryReport,
      };
    });
  }

  return axiosInstance
    .post('/mkService/reporting/v1/reports/sales_summary', data, {
      headers: { OUTLET_IDS: outletIds },
      withCredentials: false,
    })
    .then(({ data: report }) => {
      const {
        discount,
        gratuity,
        grossSales,
        netSales,
        redemption,
        refund,
        roundingAmount,
        tax,
        totalCollected,
      } = report;

      return {
        discount: discount + redemption,
        gratuity,
        grossSales,
        netSales,
        refund,
        roundingAmount,
        tax,
        totalCollected,
      };
    })
    .catch(error => {
      return Promise.reject(new Error(error.message));
    });
}

export function getSalesType(params) {
  const { startDate, endDate, outletIds } = params;
  if (hasFeature(FLAG_NAME_REPORT_USE_GRAPHQL)) {
    return queryReportingApi(print(SalesTypeQuery), {
      startDate,
      endDate,
      outletIds,
    }).then(response => {
      // Still need to do transformation
      const { salesTypeSummaryReport, salesTypeReport } = response.data.data;
      return {
        salesTypes: salesTypeReport.map(salesType => ({
          sales_type_name: salesType.salesTypeName,
          sales_type_count: salesType.numberOfTransactions,
          gross_sales: salesType.totalCollected,
        })),
        totalAmount: salesTypeSummaryReport.totalCollected,
      };
    });
  }

  return axiosInstance
    .get('/mkApi/v1/reports/sales_type', {
      params: {
        start: startDate,
        end: endDate,
      },
      withCredentials: true,
    })
    .then(res => {
      let totalAmount = 0;
      const salesTypes = res.data

        // In legacy, these types are filtered. In the new api, either we show
        // all in UI or api will do the filter
        .filter(({ sales_type_name: name }) => name && name !== 'No Sales Type')

        // Moved this calculation from view layer to here to mimic the behaviour
        // of new api
        .map(type => {
          const refund = type.refund ? type.refund : 0;
          const redemption = type.redemption ? type.redemption : 0;
          const total = Math.round(type.gross_sales - refund - redemption);
          totalAmount = totalAmount + total;
          const count =
            type.sales_type_count - type.sales_type_refund_count < 0
              ? 0
              : type.sales_type_count - type.sales_type_refund_count;

          return {
            sales_type_name: type.sales_type_name,
            sales_type_count: count,
            gross_sales: total,
          };
        });

      return {
        salesTypes,
        totalAmount,
      };
    })
    .catch(error => {
      return Promise.reject(new Error(error.message));
    });
}

export function postGrossProfit(params, outletIds) {
  const { startDate, endDate } = params;
  if (hasFeature(FLAG_NAME_REPORT_USE_GRAPHQL)) {
    return queryReportingApi(print(GrossProfitQuery), {
      startDate,
      endDate,
      outletIds,
    }).then(response => {
      const { grossProfitReport } = response.data.data;
      return grossProfitReport;
    });
  }
  return axiosInstance
    .post('/mkService/reporting/v1/reports/gross_profit', params, {
      headers: { OUTLET_IDS: outletIds },
      withCredentials: false,
    })
    .then(
      ({ data: report }) => {
        const {
          cogsAdjusment: cogsAdjustment,
          cogs,
          netSales,
          grossProfit,
          discount: oldDiscount,
          redemption,
          grossSales,
          refund,
        } = report;
        const discount = oldDiscount + redemption;

        return {
          cogsAdjustment,
          cogs,
          netSales,
          grossProfit,
          discount,
          grossSales,
          refund,
        };
      },
      error => {
        return Promise.reject(new Error(error.message));
      }
    );
}

function formatOldPaymentMethodsApiResponse(response) {
  const { data: report } = response;
  const {
    reports,
    totalCollected,
    totalNumberOfTransaction: numberOfTransactions,
  } = report;
  const categories = [
    { key: 'cash', name: 'Cash' },
    { key: 'ewallet', name: 'E-wallet' },
    { key: 'invoice', name: 'Invoice' },
    { key: 'integration', name: 'Integration' },
    { key: 'edc', name: 'EDC' },
    { key: 'card', name: 'Card' },
    { key: 'other', name: 'Other' },
  ];

  const paymentMethods = [];
  for (const category of categories) {
    const { key, name } = category;
    const entries = reports[key];

    if (entries) {
      const method = {
        categoryName: name,
      };

      // When there is only one, the entry itself is a category
      if (entries.length < 2) {
        const entry = entries[0];
        method.categoryName = entry.paymentType;
      }

      // Transform payload
      method.items = entries.map(
        ({ paymentType, collected, numberOfTransaction }) => ({
          name: paymentType,
          amount: collected,
          numberOfTransactions: numberOfTransaction,
        })
      );

      paymentMethods.push(method);
    }
  }

  return {
    numberOfTransactions,
    totalCollected,
    paymentMethods,
  };
}

function formatEvenOlderPaymentMethodsApiResponse(response) {
  const { data: report } = response;
  const {
    reports,
    totalCollected,
    totalNumberOfTransaction: numberOfTransactions,
  } = report;

  // Do some sorting
  reports.cash_or_card.reverse();
  reports.other = reports.other.sort(function(a, b) {
    const nameA = a.paymentType.toUpperCase();
    const nameB = b.paymentType.toUpperCase();
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }
    return 0;
  });

  const paymentMethods = [];
  const categories = [
    { key: 'cash_or_card' },
    { key: 'ewallet', name: 'E-wallet' },
    { key: 'edc', name: 'Invoice' },
    { key: 'other', name: 'Integration' },
    { key: 'invoice' },
  ];

  for (const category of categories) {
    const { key, name } = category;
    const entries = reports[key];

    const method = {
      categoryName: name,
    };

    // When there is only one, the entry itself is a category
    if (entries.length < 2) {
      const entry = entries[0];
      method.categoryName = entry.paymentType;
    }

    // Transform payload
    method.items = entries.map(
      ({ paymentType, collected, numberOfTransaction }) => ({
        name: paymentType,
        amount: collected,
        numberOfTransactions: numberOfTransaction,
      })
    );

    paymentMethods.push(method);
  }

  return {
    numberOfTransactions,
    totalCollected,
    paymentMethods,
  };
}

export function postPaymentMethods(data, outletIds) {
  if (hasFeature(FLAG_NAME_REPORT_USE_GRAPHQL)) {
    return queryReportingApi(print(PaymentMethodsQuery), {
      startDate: data.startDate,
      endDate: data.endDate,
      outletIds,
    }).then(response => {
      const { summary, paymentMethods } = response.data.data;

      const filteredMethods = [];

      for (const method of paymentMethods) {
        const { numberOfTransactions, items } = method;

        if (numberOfTransactions < 1) {
          continue;
        }

        const filteredItems = items.filter(
          item => item.numberOfTransactions > 0
        );

        if (filteredItems.length > 0) {
          filteredMethods.push({
            ...method,
            items: filteredItems,
          });
        }
      }

      return {
        ...summary,
        paymentMethods: filteredMethods,
      };
    });
  }

  return axiosInstance
    .post('/mkService/reporting/v1/reports/payment_methods', data, {
      headers: { OUTLET_IDS: outletIds },
      withCredentials: false,
    })
    .then(response => {
      if (hasFeature('payment-config-changes')) {
        return formatOldPaymentMethodsApiResponse(response);
      }
      return formatEvenOlderPaymentMethodsApiResponse(response);
    })
    .catch(error => {
      return Promise.reject(new Error(error.message));
    });
}

/* Item Sales Optimize Version */
export function postItemSales(data, outletIds) {
  if (hasFeature(FLAG_NAME_REPORT_USE_GRAPHQL)) {
    return queryReportingApi(print(ItemSalesQuery), {
      startDate: data.startDate,
      endDate: data.endDate,
      outletIds,
    }).then(({ data }) => data.data);
  }

  return axiosInstance
    .post('/mkService/reporting/v1/reports/item_sales', data, {
      headers: { OUTLET_IDS: outletIds },
      withCredentials: false,
    })
    .then(({ data }) => ({
      itemSalesReport: data.map(itemSale => ({
        categoryName: itemSale.category_name,
        cogs: itemSale.cogs,
        discount: itemSale.discount + itemSale.redemption,
        grossProfit: itemSale.gross_profit,
        grossSales: itemSale.gross_sales,
        itemName: itemSale.name,
        itemRefunded: itemSale.item_refunded,
        itemSold: itemSale.item_sold,
        netSales: itemSale.net_sales,
        refund: itemSale.refund,
        sku: itemSale.sku,
      })),
    }))
    .catch(error => {
      return Promise.reject(new Error(error.message));
    });
}

/* Category Sales Optimize Version */
export function postCategorySales(data, outletIds) {
  if (hasFeature(FLAG_NAME_REPORT_USE_GRAPHQL)) {
    return queryReportingApi(print(CategorySalesQuery), {
      startDate: data.startDate,
      endDate: data.endDate,
      outletIds,
    }).then(response => response.data.data);
  }

  return axiosInstance
    .post('/mkService/reporting/v1/reports/category_sales', data, {
      headers: { OUTLET_IDS: outletIds },
      withCredentials: false,
    })
    .then(({ data }) => ({
      categorySalesReport: data.map(categorySales => ({
        categoryName: categorySales.name,
        cogs: categorySales.cogs,
        discount: categorySales.discount + categorySales.redemption,
        grossProfit: categorySales.gross_profit,
        grossSales: categorySales.gross_sales,
        itemRefunded: categorySales.item_refunded,
        itemSold: categorySales.item_sold,
        netSales: categorySales.net_sales,
        refund: categorySales.refund,
      })),
    }))
    .catch(error => {
      return Promise.reject(new Error(error.message));
    });
}

/**
 * Get modifier sales.
 * @param {string} params.startDate
 * @param {string} params.endDate
 * @returns {Promise.<T>|*|Promise<R>} JSON object from server, or Error object if fails.
 */
export function getModifierSales(params) {
  const { startDate, endDate, outletIds } = params;

  let promise;
  if (hasFeature(FLAG_NAME_REPORT_USE_GRAPHQL)) {
    promise = queryReportingApi(print(ModifierSalesQuery), {
      startDate,
      endDate,
      outletIds,
    }).then(response => {
      const { modifierSales } = response.data.data;

      return modifierSales;
    });
  } else {
    promise = axiosInstance
      .get('/mkApi/v1/reports/modifiers', {
        params: { start: startDate, end: endDate },
        withCredentials: true,
      })
      .then(({ data: modifiers }) => {
        const modifierSales = modifiers.map(modifier => {
          const {
            modifier_name: name,
            quantity: quantitySold,
            gross_sales: grossSales,
            refund,
            net_sales: netSales,
            discount,
            redemption,
            modifier_options,
          } = modifier;

          const modifierOptions = modifier_options.map(option => {
            return {
              name: option.option_name,
              quantitySold: option.quantity,
              grossSales: option.gross_sales,
              refund: option.refund,
              netSales: option.net_sales,
              discount: option.discount + option.redemption,
            };
          });

          return {
            name,
            quantitySold,
            grossSales,
            refund,
            netSales,
            discount: discount + redemption,
            modifierOptions,
          };
        });

        return modifierSales;
      });
  }

  // TODO There is no way to get the total values yet. So we have to compute
  // those for both. We need to update this once API has them.
  return promise
    .then(modifierSales => {
      let totalQuantity = 0;
      let totalGrossSales = 0;
      let totalDiscount = 0;
      let totalRefund = 0;
      let totalNetSales = 0;
      for (const entry of modifierSales) {
        const { quantitySold, grossSales, discount, refund, netSales } = entry;
        totalQuantity += quantitySold;
        totalGrossSales += Math.round(grossSales);
        totalDiscount += Math.round(discount);
        totalRefund += Math.round(refund);
        totalNetSales += Math.round(netSales);
      }

      return {
        modifierSales,
        totalQuantity,
        totalGrossSales,
        totalDiscount,
        totalRefund,
        totalNetSales,
      };
    })
    .catch(error => {
      return Promise.reject(new Error(error.message));
    });
}

/**
 * Get discounts.
 * @param {string} params.startDate
 * @param {string} params.endDate
 * @param {number[]} params.outletIds
 * @returns {Promise.<T>|*|Promise<R>} JSON object from server, or Error object if fails.
 */
export function getDiscounts(params) {
  const { startDate, endDate, outletIds } = params;
  if (hasFeature(FLAG_NAME_REPORT_USE_GRAPHQL)) {
    return queryReportingApi(print(DiscountQuery), {
      startDate,
      endDate,
      outletIds,
    }).then(response => {
      const { discountReport } = response.data.data;
      return discountReport.map(discount => ({
        amount: discount.discountAmount,
        count: discount.count,
        discount_applied: discount.grossDiscount,
        discount_type: discount.discountType,
        name: discount.name,
        net_discount: discount.netDiscount,
        refund: discount.discountRefunded,
      }));
    });
  }
  return axiosInstance
    .get('/mkService/report/v1/discounts', {
      params: {
        outletIds: outletIds.join(','),
        start: startDate,
        end: endDate,
      },
      withCredentials: false,
    })
    .then(response => response.data)
    .catch(error => {
      return Promise.reject(new Error(error.message));
    });
}

/**
 * Get taxes.
 * @param {object} params
 * @param {string} params.startDate
 * @param {string} params.endDate
 * @param {number[]} params.outletIds
 * @returns {Promise.<T>|*|Promise<R>} JSON object from server, or Error object if fails.
 */
export function getTaxes(params) {
  const { startDate, endDate, outletIds } = params;

  if (hasFeature(FLAG_NAME_REPORT_USE_GRAPHQL)) {
    return queryReportingApi(print(TaxesQuery), {
      startDate,
      endDate,
      outletIds,
    }).then(response => {
      const { taxesReport, taxesSummaryReport } = response.data.data;
      return {
        reports_taxes: taxesReport.map(tax => ({
          amount: tax.taxRate,
          name: tax.taxName,
          tax_collected: tax.taxCollected,
          taxable_amount: tax.taxableAmount,
        })),
        total_tax_collected: taxesSummaryReport.taxCollected,
      };
    });
  }

  return axiosInstance
    .get('/mkApi/v1/reports/taxes', {
      params: {
        start: startDate,
        end: endDate,
      },
      withCredentials: true,
    })
    .then(response => response.data)
    .catch(error => {
      return Promise.reject(new Error(error.message));
    });
}

/**
 * Get Gratuities.
 * @param {object} params
 * @param {string} params.startDate
 * @param {string} params.endDate
 * @param {number[]} params.outletIds
 * @returns {Promise.<T>|*|Promise<R>} JSON object from server, or Error object if fails.
 */
export function getGratuities(params) {
  const { startDate, endDate, outletIds } = params;

  if (hasFeature(FLAG_NAME_REPORT_USE_GRAPHQL)) {
    return queryReportingApi(print(GratuityQuery), {
      startDate,
      endDate,
      outletIds,
    }).then(response => {
      const { gratuityReport, gratuitySummaryReport } = response.data.data;
      return {
        reports_gratuities: gratuityReport.map(gratuity => ({
          amount: gratuity.gratuityRate,
          gratuity_collected: gratuity.gratuityCollected,
          name: gratuity.gratuityName,
        })),
        total_gratuity_collected: gratuitySummaryReport.gratuityCollected,
      };
    });
  }

  return axiosInstance
    .get('/mkApi/v1/reports/gratuities', {
      params: {
        start: startDate,
        end: endDate,
      },
      withCredentials: true,
    })
    .then(response => response.data)
    .catch(error => {
      return Promise.reject(new Error(error.message));
    });
}

/**
 * Get Collected By data (staff).
 * @param {object} params
 * @param {string} params.startDate
 * @param {string} params.endDate
 * @param {number[]} params.outletIds
 * @returns {Promise.<T>|*|Promise<R>} JSON object from server, or Error object if fails.
 */
export function getStaffs(params) {
  const { startDate, endDate, outletIds } = params;

  if (hasFeature(FLAG_NAME_REPORT_USE_GRAPHQL)) {
    return queryReportingApi(print(CollectedByQuery), {
      startDate,
      endDate,
      outletIds,
    }).then(response => {
      const {
        collectedByReport,
        collectedBySummaryReport,
      } = response.data.data;
      return {
        staff_reports: collectedByReport.map(collector => ({
          staff_id: collector.collectorId,
          staff_name: collector.collectorName,
          staff_title: collector.collectorTitle,
          staff_transaction_amount: collector.totalCollected,
          staff_transaction_count: collector.numberOfTransactions,
        })),
        total_transaction_amount: collectedBySummaryReport.totalCollected,
        total_transaction_count: collectedBySummaryReport.numberOfTransactions,
      };
    });
  }

  return axiosInstance
    .get('/mkApi/v1/reports/staff_report', {
      params: {
        start: startDate,
        end: endDate,
      },
      withCredentials: true,
    })
    .then(response => response.data)
    .catch(error => {
      return Promise.reject(new Error(error.message));
    });
}

/**
 * Get servers.
 * @param {object} params
 * @param {string} params.startDate
 * @param {string} params.endDate
 * @param {number[]} params.outletIds
 * @returns {Promise.<T>|*|Promise<R>} JSON object from server, or Error object if fails.
 */
export function getServers(params) {
  const { startDate, endDate, outletIds } = params;

  if (hasFeature(FLAG_NAME_REPORT_USE_GRAPHQL)) {
    return queryReportingApi(print(ServedByQuery), {
      startDate,
      endDate,
      outletIds,
    }).then(response => {
      const { servedByReport, servedBySummaryReport } = response.data.data;
      return {
        servers: servedByReport.map(report => ({
          collected: report.totalCollected,
          number_transaction: report.numberOfTransactions,
          server_id: report.serverId,
          server_name: report.serverName,
          server_title: report.serverTitle,
        })),
        total_collected: servedBySummaryReport.totalCollected,
        total_number_transaction: servedBySummaryReport.numberOfTransactions,
      };
    });
  }

  return axiosInstance
    .get('/mkApi/v1/reports/servers', {
      params: {
        start: startDate,
        end: endDate,
      },
      withCredentials: true,
    })
    .then(response => response.data)
    .catch(error => {
      return Promise.reject(new Error(error.message));
    });
}

export function exportReport(params) {
  return axiosInstance.get('/mkService/reporting-revamp/export', {
    responseType: 'blob',
    params,
  });
}

export function exportTransaction(query) {
  return axios
    .get(`${api.EXPORT_TRANSACTION}`, { params: query, withCredentials: true })
    .catch(commonReject);
}

export function exportSummary(query, filename, outletIds, queryParams) {
  let promise;
  let extension;

  if (hasFeature(FLAG_NAME_REPORT_USE_GRAPHQL)) {
    const { startDate, endDate } = query;
    const reportType =
      outletIds.length > 1
        ? 'salesSummaryPerOutletReport'
        : 'salesSummaryReport';
    extension = 'xlsx';

    promise = exportReport({
      startDate,
      endDate,
      outletIds: outletIds.join(','),
      reportCategory: 'sales',
      reportType,
    });
  } else {
    extension = 'csv';
    promise = axios.post(api.EXPORT_SUMMARY_OPTIMIZE, query, {
      headers: { OUTLET_IDS: outletIds },
      params: queryParams,
      withCredentials: false,
    });
  }

  return promise
    .then(response => {
      fileDownload(response.data, `${filename}.${extension}`);
    })
    .catch(commonReject);
}

export function exportSalesType(query) {
  const { start, end, outletIds, filename } = query;

  if (hasFeature(FLAG_NAME_REPORT_USE_GRAPHQL)) {
    return exportReport({
      startDate: start,
      endDate: end,
      outletIds: outletIds.join(','),
      reportCategory: 'sales',
      reportType: 'salesTypeReport',
    }).then(response => {
      fileDownload(response.data, `${filename}.xlsx`);
    });
  }

  return axios
    .get(`${api.EXPORT_SALES_TYPE}`, {
      params: {
        start,
        end,
      },
      withCredentials: true,
    })
    .catch(commonReject);
}

export function exportGrossProfit(query, filename, outletIds, queryParams) {
  let promise;
  let extension;

  if (hasFeature(FLAG_NAME_REPORT_USE_GRAPHQL)) {
    const { startDate, endDate } = query;
    const reportType =
      outletIds.length > 1 ? 'grossProfitPerOutletReport' : 'grossProfitReport';
    extension = 'xlsx';

    promise = exportReport({
      startDate,
      endDate,
      outletIds: outletIds.join(','),
      reportCategory: 'sales',
      reportType,
    });
  } else {
    extension = 'csv';
    promise = axios.post(api.EXPORT_GROSS_PROFIT_OPTIMIZE, query, {
      headers: { OUTLET_IDS: outletIds },
      params: queryParams,
      withCredentials: false,
    });
  }

  return promise
    .then(response => {
      fileDownload(response.data, `${filename}.${extension}`);
    })
    .catch(commonReject);
}

export function exportHourlyItemSold(query, filename, outletIds) {
  let promise;
  let extension;

  if (hasFeature(FLAG_NAME_REPORT_USE_GRAPHQL)) {
    const { startDate, endDate } = query;
    extension = 'xlsx';

    promise = exportReport({
      startDate,
      endDate,
      outletIds: outletIds.join(','),
      reportCategory: 'items',
      reportType: 'itemSoldHourlyReport',
    });
  } else {
    extension = 'csv';
    promise = axios.post(api.EXPORT_HOURLY_ITEM_SOLD, query, {
      headers: { OUTLET_IDS: outletIds },
      withCredentials: false,
    });
  }
  return promise
    .then(response => {
      fileDownload(response.data, `${filename}.${extension}`);
    })
    .catch(commonReject);
}

export function exportHourlyItemAmount(query, filename, outletIds) {
  let promise;
  let extension;

  if (hasFeature(FLAG_NAME_REPORT_USE_GRAPHQL)) {
    const { startDate, endDate } = query;
    extension = 'xlsx';

    promise = exportReport({
      startDate,
      endDate,
      outletIds: outletIds.join(','),
      reportCategory: 'items',
      reportType: 'amountSoldHourlyReport',
    });
  } else {
    extension = 'csv';
    promise = axios.post(api.EXPORT_HOURLY_ITEM_AMOUNT, query, {
      headers: { OUTLET_IDS: outletIds },
      withCredentials: false,
    });
  }

  return promise
    .then(response => {
      fileDownload(response.data, `${filename}.${extension}`);
    })
    .catch(commonReject);
}

export function exportPaymentMethod(query, filename, outletIds, queryParams) {
  let promise;
  let extension;

  if (hasFeature(FLAG_NAME_REPORT_USE_GRAPHQL)) {
    const { startDate, endDate } = query;
    const reportType =
      outletIds.length > 1
        ? 'paymentMethodPerOutletReport'
        : 'paymentMethodReport';
    extension = 'xlsx';

    promise = exportReport({
      startDate,
      endDate,
      outletIds: outletIds.join(','),
      reportCategory: 'sales',
      reportType,
    });
  } else {
    promise = axios
      .post(api.EXPORT_PAYMENT_METHOD_OPTIMIZE, query, {
        headers: { OUTLET_IDS: outletIds, Accept: '*/*' },
        params: queryParams,
        withCredentials: false,
        responseType: 'blob',
      })
      .then(response => {
        extension =
          response.headers['content-type'] ===
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            ? '.xlsx'
            : '.csv';
        return response;
      });
  }

  return promise
    .then(response => {
      fileDownload(response.data, `${filename}.${extension}`);
    })
    .catch(commonReject);
}

export function exportItemSales(query, filename, outletIds) {
  let promise;
  let extension;

  if (hasFeature(FLAG_NAME_REPORT_USE_GRAPHQL)) {
    const { startDate, endDate } = query;
    extension = 'xlsx';

    promise = exportReport({
      startDate,
      endDate,
      outletIds: outletIds.join(','),
      reportCategory: 'items',
      reportType: 'itemSalesReport',
    });
  } else {
    extension = 'csv';
    promise = axios.post(api.EXPORT_ITEM_SALES_OPTIMIZE, query, {
      headers: { OUTLET_IDS: outletIds },
      withCredentials: false,
    });
  }

  return promise
    .then(response => {
      fileDownload(response.data, `${filename}.${extension}`);
    })
    .catch(commonReject);
}

export function exportCategorySales(query, filename, outletIds) {
  let promise;
  let extension;

  if (hasFeature(FLAG_NAME_REPORT_USE_GRAPHQL)) {
    const { startDate, endDate } = query;
    extension = 'xlsx';

    promise = exportReport({
      startDate,
      endDate,
      outletIds: outletIds.join(','),
      reportCategory: 'items',
      reportType: 'categorySalesReport',
    });
  } else {
    extension = 'csv';
    promise = axios.post(api.EXPORT_CATEGORY_SALES_OPTIMIZE, query, {
      headers: { OUTLET_IDS: outletIds },
      withCredentials: false,
    });
  }

  return promise
    .then(response => {
      fileDownload(response.data, `${filename}.${extension}`);
    })
    .catch(commonReject);
}

export function exportModifierSales(query) {
  const { start, end, outletIds, filename } = query;

  if (hasFeature(FLAG_NAME_REPORT_USE_GRAPHQL)) {
    return exportReport({
      startDate: start,
      endDate: end,
      outletIds: outletIds.join(','),
      reportType: 'modifierSalesReport',
    }).then(response => {
      fileDownload(response.data, `${filename}.xlsx`);
    });
  }

  return axios
    .get(`${api.EXPORT_MODIFIER_SALES}`, {
      params: {
        start,
        end,
      },
      withCredentials: true,
    })
    .catch(commonReject);
}

export function exportDiscounts(query) {
  const { start, end, outletIds, filename } = query;
  let promise;
  let extension;

  if (hasFeature(FLAG_NAME_REPORT_USE_GRAPHQL)) {
    extension = 'xlsx';

    promise = exportReport({
      startDate: start,
      endDate: end,
      outletIds: outletIds.join(','),
      reportType: 'discountReport',
    });
  } else {
    extension = 'csv';
    promise = axios.get(api.EXPORT_DISCOUNT, {
      params: {
        start,
        end,
        outletIds: outletIds.join(','),
      },
      withCredentials: false,
    });
  }

  return promise
    .then(response => {
      fileDownload(response.data, `${filename}.${extension}`);
    })
    .catch(commonReject);
}

export function exportTaxes(query) {
  const { start, end, outletIds, filename } = query;

  if (hasFeature(FLAG_NAME_REPORT_USE_GRAPHQL)) {
    return exportReport({
      startDate: start,
      endDate: end,
      outletIds: outletIds.join(','),
      reportType: 'taxesReport',
    }).then(response => {
      fileDownload(response.data, `${filename}.xlsx`);
    });
  }

  return axios
    .get(`${api.EXPORT_TAXES}`, {
      params: {
        start,
        end,
      },
      withCredentials: true,
    })
    .catch(commonReject);
}

export function exportGratuity(query) {
  const { start, end, outletIds, filename } = query;

  if (hasFeature(FLAG_NAME_REPORT_USE_GRAPHQL)) {
    return exportReport({
      startDate: start,
      endDate: end,
      outletIds: outletIds.join(','),
      reportType: 'gratuityReport',
    }).then(response => {
      fileDownload(response.data, `${filename}.xlsx`);
    });
  }

  return axios
    .get(`${api.EXPORT_GRATUITY}`, {
      params: {
        start,
        end,
      },
      withCredentials: true,
    })
    .catch(commonReject);
}

export function exportColectedBy(query) {
  const { start, end, outletIds, filename } = query;

  if (hasFeature(FLAG_NAME_REPORT_USE_GRAPHQL)) {
    return exportReport({
      startDate: start,
      endDate: end,
      outletIds: outletIds.join(','),
      reportType: 'collectedByReport',
    }).then(response => {
      fileDownload(response.data, `${filename}.xlsx`);
    });
  }

  return axios
    .get(`${api.EXPORT_COLLECTEDBY}`, {
      params: {
        start,
        end,
      },
      withCredentials: true,
    })
    .catch(commonReject);
}

export function exportServedBy(query) {
  const { start, end, outletIds, filename } = query;

  if (hasFeature(FLAG_NAME_REPORT_USE_GRAPHQL)) {
    return exportReport({
      startDate: start,
      endDate: end,
      outletIds: outletIds.join(','),
      reportType: 'servedByReport',
    }).then(response => {
      fileDownload(response.data, `${filename}.xlsx`);
    });
  }

  return axios
    .get(`${api.EXPORT_SERVEDBY}`, {
      params: {
        start,
        end,
      },
      withCredentials: true,
    })
    .catch(commonReject);
}
